import React from "react";
import { UserCircle, UsersThree } from "@phosphor-icons/react";

/**
 * AddedByChip — compact chip showing which recruiter owns a candidate.
 *
 * Used across:
 * - CandidatesList row / grid card
 * - Kanban card
 * - CandidateDrawer + PersonDetailPanel header
 * - Interviews list
 *
 * Props:
 *   name          — recruiter's display name ("Ravi Kumar")
 *   isTeamPool    — boolean; if true renders a "Team pool" chip instead
 *   isMine        — boolean; renders "You" + emphasized styling when the
 *                    row belongs to the current user (recruiters love this)
 *   size          — "sm" | "md"; sm is used in dense tables, md in headers
 *   testId        — data-testid override (defaults to `added-by-chip`)
 */
export default function AddedByChip({
    name,
    isTeamPool = false,
    isMine = false,
    size = "sm",
    testId = "added-by-chip",
    className = "",
}) {
    if (!name && !isTeamPool && !isMine) return null;
    // Team pool always wins over "You" — a shared row isn't personally owned.
    const showMine = isMine && !isTeamPool;

    const px = size === "md" ? "px-2.5 py-1" : "px-1.5 py-0.5";
    const fs = size === "md" ? "text-[11px]" : "text-[10px]";
    const ic = size === "md" ? 12 : 10;

    if (isTeamPool) {
        return (
            <span
                data-testid={`${testId}-team-pool`}
                className={`inline-flex items-center gap-1 ${px} ${fs} font-bold uppercase tracking-wider bg-[#EFF6FF] text-[#1F7AEC] border border-[#DBEAFE] ${className}`}
                title="Available to all recruiters in the workspace"
            >
                <UsersThree size={ic} weight="bold" />
                Team pool
            </span>
        );
    }

    const mineCls = showMine
        ? "bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0]"
        : "bg-[#F5F5F5] text-[#525252] border border-[#E5E5E5]";

    return (
        <span
            data-testid={`${testId}${showMine ? "-mine" : ""}`}
            className={`inline-flex items-center gap-1 ${px} ${fs} font-bold uppercase tracking-wider ${mineCls} ${className}`}
            title={showMine ? "Added by you" : `Added by ${name}`}
        >
            <UserCircle size={ic} weight="bold" />
            {showMine ? "You" : name}
        </span>
    );
}
